import React, { useState } from 'react';
import { HelpCircle, X, Mic, Smartphone, Monitor } from 'lucide-react';
import { Button } from './button';

/**
 * MicrophoneHelp - A reusable component for microphone access help
 * 
 * Usage:
 * <MicrophoneHelp error={micError} />
 * 
 * Props:
 * - error: string | null - The current microphone error message (optional)
 * - className: string - Additional CSS classes (optional)
 */
const MicrophoneHelp = ({ error, className = '' }) => {
  const [showModal, setShowModal] = useState(false);

  // Determine if we should show the blocked warning
  const isBlocked = error && (
    error.toLowerCase().includes('blocked') || 
    error.toLowerCase().includes('not-allowed') ||
    error.toLowerCase().includes('denied') ||
    error.toLowerCase().includes('permission')
  );

  return (
    <>
      {/* Inline Warning + Help Link */}
      <div className={`space-y-2 ${className}`}>
        {/* Show error/blocked warning if present - gentler styling */}
        {error && (
          <div className="p-3 rounded-lg border bg-slate-50 border-slate-200 text-center">
            <p className="text-sm text-slate-600">
              {isBlocked 
                ? 'It appears your microphone is blocked. Click the lock icon near the address bar and allow microphone access.'
                : error
              }
            </p>
          </div>
        )}

        {/* Help link - always visible when there's an error */}
        {error && (
          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="flex items-center justify-center gap-1.5 w-full text-sm text-purple-600 hover:text-purple-800 hover:underline transition-colors"
          >
            <HelpCircle className="w-4 h-4" />
            Need help with microphone access?
          </button>
        )}
      </div>

      {/* Help Modal - positioned at top of viewport so tip is visible */}
      {showModal && (
        <div 
          className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 p-4 pt-12 overflow-y-auto"
          onClick={() => setShowModal(false)}
        >
          <div 
            className="bg-white rounded-xl shadow-xl max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header - softer purple */}
            <div className="flex items-center justify-between p-4 border-b border-slate-100 bg-slate-50 rounded-t-xl">
              <div className="flex items-center gap-2">
                <Mic className="w-5 h-5 text-purple-500" />
                <h3 className="font-semibold text-base text-slate-700">Microphone Help</h3>
              </div>
              <button 
                onClick={() => setShowModal(false)}
                className="p-1.5 hover:bg-slate-100 rounded-full transition-colors"
              >
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>

            {/* Content - simplified */}
            <div className="p-4 space-y-4">
              <p className="text-slate-600 text-sm">
                To use voice, your browser needs microphone access.
              </p>

              {/* Desktop Instructions */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-slate-600 font-medium text-sm">
                  <Monitor className="w-4 h-4 text-purple-400" />
                  <span>On laptop / desktop:</span>
                </div>
                <ol className="ml-6 space-y-1 text-sm text-slate-500 list-decimal list-outside">
                  <li>Click the lock icon near the address bar</li>
                  <li>Find Microphone and set to Allow</li>
                  <li>Refresh the page</li>
                </ol>
              </div>

              {/* Mobile Instructions */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-slate-600 font-medium text-sm">
                  <Smartphone className="w-4 h-4 text-purple-400" />
                  <span>On phone:</span>
                </div>
                <ol className="ml-6 space-y-1 text-sm text-slate-500 list-decimal list-outside">
                  <li>Open browser settings</li>
                  <li>Allow microphone for this site</li>
                  <li>Re-open the page</li>
                </ol>
              </div>

              {/* Tip - gentler styling */}
              <div className="bg-slate-50 border border-slate-100 rounded-lg p-3">
                <p className="text-xs text-slate-500">
                  <span className="font-medium">Tip:</span> Close and reopen the page after allowing access if it still doesn't work.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-100">
              <Button
                onClick={() => setShowModal(false)}
                className="w-full bg-purple-500 hover:bg-purple-600 h-10"
              >
                Got it
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/**
 * MicrophoneHelpLink - A standalone help link without the error display
 * Use this when you want to show just the help link (e.g., near the mic button)
 */
export const MicrophoneHelpLink = ({ className = '' }) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setShowModal(true)}
        className={`inline-flex items-center gap-1 text-xs text-slate-400 hover:text-purple-600 transition-colors ${className}`}
      >
        <HelpCircle className="w-3 h-3" />
        Mic help
      </button>

      {/* Reuse the same modal - positioned at top */}
      {showModal && (
        <div 
          className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 p-4 pt-12 overflow-y-auto"
          onClick={() => setShowModal(false)}
        >
          <div 
            className="bg-white rounded-xl shadow-xl max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-100 bg-slate-50 rounded-t-xl">
              <div className="flex items-center gap-2">
                <Mic className="w-5 h-5 text-purple-500" />
                <h3 className="font-semibold text-base text-slate-700">Microphone Help</h3>
              </div>
              <button 
                onClick={() => setShowModal(false)}
                className="p-1.5 hover:bg-slate-100 rounded-full transition-colors"
              >
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              <p className="text-slate-600 text-sm">
                To use voice, your browser needs microphone access.
              </p>

              {/* Desktop Instructions */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-slate-600 font-medium text-sm">
                  <Monitor className="w-4 h-4 text-purple-400" />
                  <span>On laptop / desktop:</span>
                </div>
                <ol className="ml-6 space-y-1 text-sm text-slate-500 list-decimal list-outside">
                  <li>Click the lock icon near the address bar</li>
                  <li>Find Microphone and set to Allow</li>
                  <li>Refresh the page</li>
                </ol>
              </div>

              {/* Mobile Instructions */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-slate-600 font-medium text-sm">
                  <Smartphone className="w-4 h-4 text-purple-400" />
                  <span>On phone:</span>
                </div>
                <ol className="ml-6 space-y-1 text-sm text-slate-500 list-decimal list-outside">
                  <li>Open browser settings</li>
                  <li>Allow microphone for this site</li>
                  <li>Re-open the page</li>
                </ol>
              </div>

              {/* Tip */}
              <div className="bg-slate-50 border border-slate-100 rounded-lg p-3">
                <p className="text-xs text-slate-500">
                  <span className="font-medium">Tip:</span> Close and reopen the page after allowing access if it still doesn't work.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-100">
              <Button
                onClick={() => setShowModal(false)}
                className="w-full bg-purple-500 hover:bg-purple-600 h-10"
              >
                Got it
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MicrophoneHelp;
